import { useQuery, useMutation, type UseQueryOptions } from '@tanstack/react-query';
import { apiClient } from '../services/apiClient';

export interface OrgDeleteImpact {
  organizationId: string;
  organizationName: string;
  ownerEmail: string | null;
  ownerFullName: string | null;
  subscriptionPlan: string | null;
  subscriptionStatus: string | null;
  locations: number;
  members: number;
  playlists: number;
  scenarios: number;
  tracks: number;
  alreadyDeleting: boolean;
}

export interface OrgDeletionStatus {
  id: string;
  organizationId: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  phase: string | null;
  error: string | null;
  requestedAt: string;
  completedAt: string | null;
  organizationName: string;
  ownerEmail: string | null;
}

export function getOrgDeleteImpact(orgId: string): Promise<OrgDeleteImpact> {
  return apiClient.get<OrgDeleteImpact>(`/admin/organizations/${orgId}/delete-impact`);
}

export function requestOrgDelete(orgId: string, confirmName: string): Promise<OrgDeletionStatus> {
  return apiClient.post<OrgDeletionStatus>(`/admin/organizations/${orgId}/delete`, { confirmName });
}

export function getOrgDeleteStatus(orgId: string): Promise<OrgDeletionStatus> {
  return apiClient.get<OrgDeletionStatus>(`/admin/organizations/${orgId}/delete-status`);
}

export function useOrgDeleteImpact(
  orgId: string,
  options?: Partial<UseQueryOptions<OrgDeleteImpact>>,
) {
  return useQuery<OrgDeleteImpact>({
    queryKey: ['admin-org-delete-impact', orgId],
    queryFn: () => getOrgDeleteImpact(orgId),
    ...options,
  });
}

export function useRequestOrgDelete() {
  return useMutation({
    mutationFn: ({ orgId, confirmName }: { orgId: string; confirmName: string }) =>
      requestOrgDelete(orgId, confirmName),
  });
}

export function useOrgDeleteStatus(
  orgId: string,
  options?: Partial<UseQueryOptions<OrgDeletionStatus>>,
) {
  return useQuery<OrgDeletionStatus>({
    queryKey: ['admin-org-delete-status', orgId],
    queryFn: () => getOrgDeleteStatus(orgId),
    staleTime: 0, // always reflect live job state while polling
    ...options,
  });
}
